import React, { useEffect, useReducer } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ProductLandingContext } from "../../../utils/stateHandlers/contexts";
import { api } from "../../../utils";
import states from "../../../utils/stateHandlers/initialStates";
import { landingReducer } from "../../../utils/stateHandlers/reducers";
import _ from "lodash";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { Mobile, Default } from "../../../utils/deviceHelpers";
import ProductTiles from "./ProductTiles";
import ProductFilter from "./ProductFilter";

export default function ProductLandingPage(props) {
  const [landingState, landingDispatch] = useReducer(
    landingReducer,
    states.initialProductLandingState
  );
  const {
    pageLoaded,
    itemsLoaded,
    showFilters,
    filters,
    bestSellers,
    products,
    searchQuery,
  } = landingState;
  const category = props.Category;
  const isMill = props.isMill;

  function LoadingIndicator() {
    return (
      <div id="ghost-element">
        <Mobile>
          <Grid container>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Skeleton variant="rectangular" width={210} height={118} />
                <Skeleton variant="text" />
              </Stack>
            </Grid>
          </Grid>
        </Mobile>
        <Default>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ marginTop: "3rem" }}
          >
            <Stack spacing={1}>
              <Skeleton variant="rectangular" width={210} height={118} />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </Stack>
            <Stack spacing={1}>
              <Skeleton variant="rectangular" width={210} height={118} />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </Stack>
            <Stack spacing={1}>
              <Skeleton variant="rectangular" width={210} height={118} />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </Stack>
            <Stack spacing={1}>
              <Skeleton variant="rectangular" width={210} height={118} />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </Stack>
          </Stack>
        </Default>
      </div>
    );
  }

  function updateFilterValues(newFilters) {
    _.forEach(newFilters, function (newFilter) {
      const oldFilter = _.find(filters, function (filter) {
        return filter.name === newFilter.name;
      });
      return (oldFilter.values = newFilter.values);
    });
  }

  function filterBestSellers() {
    const bsFilter = _.find(searchQuery.fields, function (searchField) {
      return searchField.name === "BestSeller";
    });
    const bsFilterField = _.find(filters, function (filter) {
      return filter.name === "BestSeller";
    });
    if (bsFilter) {
      if (bsFilter.values.length > 0) {
        bsFilter.values = [];
        landingDispatch({
          type: "updateSearchQuery",
          payload: searchQuery.fields,
        });
        landingDispatch({ type: "showBestSellers", payload: false });
      } else {
        bsFilter.values = [bsFilterField.values[0].value];
        landingDispatch({
          type: "updateSearchQuery",
          payload: searchQuery.fields,
        });
        landingDispatch({ type: "showBestSellers", payload: true });
      }
    } else {
      const fields = _.concat(searchQuery.fields, {
        name: "BestSeller",
        values: [bsFilterField.values[0].value],
      });
      landingDispatch({ type: "updateSearchQuery", payload: fields });
      landingDispatch({ type: "showBestSellers", payload: true });
    }
  }

  useEffect(() => {
    landingDispatch({ type: "setPageLoaded", payload: false });
    landingDispatch({ type: "setItemsLoaded", payload: false });
    api
      .post("catalog/items", {
        category: category,
        filters: [],
      })
      .then((response) => {
        landingDispatch({ type: "setFilters", payload: response.data.groups });
        landingDispatch({ type: "setProducts", payload: response.data.items });
        landingDispatch({ type: "setPageLoaded", payload: true });
        landingDispatch({ type: "setItemsLoaded", payload: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    if (pageLoaded) {
      landingDispatch({ type: "setItemsLoaded", payload: false });
      api
        .post("catalog/items", {
          category: category,
          filters: searchQuery.fields,
        })
        .then((response) => {
          updateFilterValues(response.data.groups);
          landingDispatch({
            type: "setProducts",
            payload: response.data.items,
          });
          landingDispatch({ type: "setItemsLoaded", payload: true });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  return (
    <ProductLandingContext.Provider value={{ landingState, landingDispatch }}>
      {!pageLoaded ? (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <>
          <Mobile>
            <Stack justifyContent="center" alignItems="center">
              {!showFilters ? (
                <Chip
                  color="primary"
                  label="Show Filters"
                  icon={<ArrowDropDownIcon />}
                  sx={{ paddingLeft: ".5rem" }}
                  onClick={() =>
                    landingDispatch({ type: "setShowFilters", payload: true })
                  }
                />
              ) : (
                <Chip
                  color="primary"
                  label="Hide Filters"
                  icon={<ArrowDropUpIcon />}
                  sx={{ paddingLeft: ".5rem" }}
                  onClick={() =>
                    landingDispatch({ type: "setShowFilters", payload: false })
                  }
                />
              )}
            </Stack>
            <Collapse in={showFilters}>
              <Stack direction="row">
                {bestSellers ? (
                  <Chip
                    color="success"
                    label="Show All"
                    sx={{ marginRight: ".5rem" }}
                    onClick={() => filterBestSellers()}
                  />
                ) : (
                  <Chip
                    color="primary"
                    variant="outlined"
                    label="Show Only Best Sellers"
                    sx={{ marginRight: ".5rem" }}
                    onClick={() => filterBestSellers()}
                  />
                )}
                <Chip
                  color="warning"
                  label="Reset Filters"
                  sx={{ paddingLeft: ".5rem" }}
                  onClick={() => {
                    landingDispatch({ type: "updateSearchQuery", payload: [] });
                    landingDispatch({ type: "setClearAll", payload: true });
                  }}
                />
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    sm: "repeat(1, 1fr)",
                    md: "repeat(6, 1fr)",
                    xl: "repeat(6, 1fr)",
                  },
                }}
              >
                {filters &&
                  filters.map(
                    (filter) =>
                      filter.name !== "BestSeller" && (
                        <ProductFilter filter={filter} key={filter.name} />
                      )
                  )}
              </Box>
            </Collapse>
          </Mobile>
          <Default>
            <Stack spacing={1} className="sticky-filters">
              <Grid container sx={{ paddingTop: "1rem" }}>
                <Grid item xs={9}>
                  <Typography variant="h1">{category}</Typography>
                </Grid>
                {!isMill && (
                  <Grid item xs={3}>
                    <Chip
                      className="float-right"
                      color="warning"
                      label="Reset Filters"
                      onClick={() => {
                        landingDispatch({
                          type: "updateSearchQuery",
                          payload: [],
                        });
                        landingDispatch({ type: "setClearAll", payload: true });
                      }}
                    />
                    {filters &&
                      filters.map((filter) => (
                        <React.Fragment key={filter.name}>
                          {filter.name === "BestSeller" && (
                            <>
                              {bestSellers ? (
                                <Chip
                                  className="float-right"
                                  color="success"
                                  label="Show All"
                                  sx={{ marginRight: ".5rem" }}
                                  onClick={() => filterBestSellers()}
                                />
                              ) : (
                                <Chip
                                  className="float-right"
                                  color="primary"
                                  variant="outlined"
                                  label="Show Only Best Sellers"
                                  sx={{ marginRight: ".5rem" }}
                                  onClick={() => filterBestSellers()}
                                />
                              )}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                  </Grid>
                )}
              </Grid>
              {!isMill && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      sm: "repeat(1, 1fr)",
                      md: "repeat(6, 1fr)",
                      xl: "repeat(6, 1fr)",
                    },
                  }}
                >
                  {filters &&
                    filters.map(
                      (filter) =>
                        filter.name !== "BestSeller" && (
                          <ProductFilter filter={filter} key={filter.name} />
                        )
                    )}
                </Box>
              )}
            </Stack>
          </Default>
          {!itemsLoaded ? (
            <LoadingIndicator />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  sm: "repeat(1, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(4, 1fr)",
                  xl: "repeat(4, 1fr)",
                },
              }}
            >
              {products
                ? products.map((data, index) => (
                    <ProductTiles key={index} data={data} category={category} />
                  ))
                : "No Products to List, please try different filters"}
            </Box>
          )}
        </>
      )}
    </ProductLandingContext.Provider>
  );
}
